import React from 'react';
import type { DateInput } from '@koob/dates';
import { formatCurrency } from '@koob/utils';
import { ExtraScopeEnum } from '@koob/enums';
import ExternalFactsheetLink from '../ExternalFactsheetLink';
import { AdditionalServicesBandScopeEnum } from '../AdditionalServicesBand';
import TripReadOnlyServicesBand from './TripReadOnlyServicesBand';
import { useVersionExperienceDayPrice } from '../../hooks/useTripVersionDayPrices';
import type { Translation } from './types';

export default function TripDayReadOnlyExperience({
  experience,
  experienceDayIndex,
  extras,
  date,
  itineraryLabel,
  departureDate,
  frame,
  t,
}: {
  experience: any;
  experienceDayIndex?: number;
  extras: any[];
  date: DateInput;
  itineraryLabel: string;
  departureDate: DateInput;
  frame: any;
  t: Translation;
}) {
  const { price, isLoading } = useVersionExperienceDayPrice({
    experience,
    experienceDayIndex,
    extras,
    date,
  });

  const dayExtras = extras.filter(
    (extra) => extra?.scope === ExtraScopeEnum.DAY,
  );

  return (
    <div className="flex flex-col gap-1.5">
      {/* KD-3678 — the version panel reads like the live day: the orange bar
          marks the program, the name carries it and the itinerary qualifies
          that name just under it, instead of floating above the card as a row
          of its own. */}
      <div className="flex gap-2 pr-1.5">
        <div className="w-1 shrink-0 rounded-full bg-orange-500" />

        <div className="min-w-0">
          {/* The factsheet icon lives on the frame header and only there:
              repeated on every day of an instance it read as a control of the
              day rather than of the program. */}
          <div className="text-sm font-medium text-gray-900">
            <ExternalFactsheetLink name={experience?.name ?? ''} />
          </div>

          <div className="text-xs text-gray-500">{itineraryLabel}</div>

          {/* §9 — the price of an instance is stated once, on its first day. */}
          {!isLoading && experienceDayIndex === 1 && price ? (
            <div
              className="text-xs text-blue-500"
              data-testid="version-day-experience-price"
            >
              {formatCurrency({
                amount: price.price,
                currency: price.currency,
              })}
            </div>
          ) : null}
        </div>
      </div>

      <TripReadOnlyServicesBand
        scope={AdditionalServicesBandScopeEnum.DAY}
        extras={dayExtras}
        departureDate={departureDate}
        frame={frame}
        t={t}
      />
    </div>
  );
}
